/**
 * <mh-breadcrumb> — the client half of snippets/breadcrumb.liquid.
 *
 * It does two jobs, and on most page loads it does neither, leaving the
 * server-rendered trail exactly as it is:
 *
 *   1. Upgrade. The Liquid walk cannot know which route the shopper took. For
 *      a product reached from search or a recommendation it falls back to the
 *      product's first collection alphabetically, which is a deliberate,
 *      predictable choice but not a record of anything. When
 *      assets/menu-path.js recorded the branch the shopper actually walked,
 *      and that branch leads to a collection this product is in, the crumbs
 *      are rewritten to follow it.
 *
 *   2. Build. Liquid now always renders something, but where the runtime
 *      cannot parse the menu tree (no parse_json) that something is only the
 *      fallback crumb — no data-tree-matched. The full trail is built here
 *      instead, from the same tree and the same path Liquid tried to match.
 *
 * Both paths render identical markup, microdata included, so the two never
 * disagree visually.
 */

import { normalizePath, loadMenuTree, collectTrails, pickTrail, getFollowedPath, resolveTrail } from '@theme/menu-path';

class MhBreadcrumb extends HTMLElement {
  async connectedCallback() {
    const isProduct = this.hasAttribute('data-product');

    const trail = isProduct ? await this.#productTrail() : await this.#pageTrail();

    // Nothing better to show than what the server rendered.
    if (!trail || trail.length === 0) return;
    if (this.#matchesRendered(trail, isProduct)) return;

    this.#render(trail, isProduct);
  }

  /**
   * The trail for a product: the collection the shopper browsed through wins,
   * then the collection-scoped URL they arrived on, then nothing (the Liquid
   * fallback stands).
   *
   * @returns {Promise<Array<{ title: string, url: string }> | null>}
   */
  async #productTrail() {
    const candidates = new Set((this.dataset.candidates || '').split('|').filter(Boolean));
    const followed = getFollowedPath();

    // The route the shopper walked, when it leads somewhere this product lives.
    if (followed && candidates.has(followed.url)) return followed.trail;

    // Otherwise Liquid already picked the collection — the one the shopper came
    // through, or the first alphabetically. Resolving its branch here only adds
    // anything when Liquid could not walk the tree itself.
    if (!this.hasAttribute('data-tree-matched')) return resolveTrail(this.dataset.matchPath || '');

    return null;
  }

  /**
   * The trail for a collection or page. Worth resolving when the shopper
   * walked here through a branch — the Liquid walk matches the current path
   * exactly, but it matches the FIRST branch containing it, which is not
   * necessarily the one they used — or when Liquid could not walk at all.
   *
   * @returns {Promise<Array<{ title: string, url: string }> | null>}
   */
  async #pageTrail() {
    const currentPath = this.dataset.currentPath || '';
    if (!currentPath) return null;

    const followed = getFollowedPath();
    if (followed && followed.url === currentPath) return followed.trail;

    if (this.hasAttribute('data-tree-matched')) return null;

    const tree = await loadMenuTree();
    if (!tree) return null;
    return pickTrail(collectTrails(tree.items, currentPath), null);
  }

  /**
   * True when the rendered crumbs already say what `trail` says, so a load
   * that changes nothing does not reflow the page.
   *
   * @param {Array<{ title: string, url: string }>} trail
   * @param {boolean} isProduct
   */
  #matchesRendered(trail, isProduct) {
    const names = [...this.querySelectorAll('.mh-breadcrumb__item [itemprop="name"]')].map(
      (node) => node.textContent?.trim() ?? ''
    );
    // Drop the Home crumb, and the product's own title where present.
    const rendered = names.slice(1, isProduct ? names.length - 1 : names.length);

    return (
      rendered.length === trail.length && rendered.every((name, index) => name === (trail[index].title || '').trim())
    );
  }

  /**
   * Renders the trail with the same markup and schema.org microdata as the
   * server-side path in snippets/breadcrumb.liquid.
   *
   * @param {Array<{ title: string, url: string }>} trail
   * @param {boolean} isProduct
   */
  #render(trail, isProduct) {
    const nav = document.createElement('nav');
    nav.className = 'mh-breadcrumb';
    nav.setAttribute('aria-label', this.dataset.ariaLabel || 'Breadcrumbs');

    const list = document.createElement('ol');
    list.className = 'mh-breadcrumb__list list-unstyled';
    list.setAttribute('itemscope', '');
    list.setAttribute('itemtype', 'https://schema.org/BreadcrumbList');

    let position = 0;

    const addCrumb = (name, url, isCurrent) => {
      position += 1;
      const item = document.createElement('li');
      item.className = `mh-breadcrumb__item${isCurrent ? ' mh-breadcrumb__item--current' : ''}`;
      item.setAttribute('itemprop', 'itemListElement');
      item.setAttribute('itemscope', '');
      item.setAttribute('itemtype', 'https://schema.org/ListItem');

      if (position > 1) {
        const separator = document.createElement('span');
        separator.className = 'mh-breadcrumb__separator';
        separator.setAttribute('aria-hidden', 'true');
        separator.textContent = '/';
        item.appendChild(separator);
      }

      if (!isCurrent && url) {
        const link = document.createElement('a');
        link.className = 'mh-breadcrumb__link';
        link.href = url;
        link.setAttribute('itemprop', 'item');
        const label = document.createElement('span');
        label.setAttribute('itemprop', 'name');
        label.textContent = name;
        link.appendChild(label);
        item.appendChild(link);
      } else {
        const current = document.createElement('span');
        current.className = 'mh-breadcrumb__current';
        current.setAttribute('itemprop', 'name');
        if (isCurrent) current.setAttribute('aria-current', 'page');
        current.textContent = name;
        item.appendChild(current);
      }

      const meta = document.createElement('meta');
      meta.setAttribute('itemprop', 'position');
      meta.setAttribute('content', String(position));
      item.appendChild(meta);

      list.appendChild(item);
    };

    addCrumb(this.dataset.homeLabel || 'Home', this.dataset.rootUrl || '/', false);

    trail.forEach((node, index) => {
      const isLastNode = index === trail.length - 1;
      const isCurrent = isLastNode && !isProduct;
      addCrumb(node.title || '', normalizePath(node.url || ''), isCurrent);
    });

    if (isProduct) addCrumb(this.dataset.productTitle || '', '', true);

    nav.appendChild(list);
    this.replaceChildren(nav);
  }
}

if (!customElements.get('mh-breadcrumb')) {
  customElements.define('mh-breadcrumb', MhBreadcrumb);
}

export { MhBreadcrumb };
